import { useCallback } from 'react';
import type { PhieuXuatKho, PhieuXuatKhoInput } from '@/types/schemas';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

interface UsePhieuXuatKhoMutationProps {
  detail: PhieuXuatKho | null | undefined;
  formMode: 'add' | 'edit' | 'view';
  selectedObj: PhieuXuatKho | null | undefined;
  addItem: (data: PhieuXuatKhoInput) => Promise<PhieuXuatKho>;
  updateItem: (uuid: string, data: PhieuXuatKhoInput) => Promise<PhieuXuatKho>;
  deleteItem: (uuid: string) => Promise<unknown>;
  handleCloseForm: () => void;
  handleCloseDelete: () => void;
  clearSelection: () => void;
  refreshData: () => Promise<void>;
}

export const usePhieuXuatKhoMutation = ({
  detail,
  formMode,
  selectedObj,
  addItem,
  updateItem,
  deleteItem,
  handleCloseForm,
  handleCloseDelete,
  clearSelection,
  refreshData
}: UsePhieuXuatKhoMutationProps) => {
  const { entity } = useAuth();

  const handleSubmit = useCallback(
    async (data: PhieuXuatKhoInput) => {
      try {
        if (formMode === 'add') {
          await addItem(data);
        } else if (formMode === 'edit' && selectedObj?.uuid) {
          await updateItem(selectedObj.uuid, data);
        }

        handleCloseForm();
        clearSelection();
        await refreshData();
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [addItem, clearSelection, formMode, handleCloseForm, refreshData, selectedObj?.uuid, updateItem]
  );

  const handleDelete = useCallback(async () => {
    const targetUuid = detail?.uuid ?? selectedObj?.uuid;

    if (!targetUuid) return;

    try {
      handleCloseForm();
      await deleteItem(targetUuid);
      handleCloseDelete();
      clearSelection();
      await refreshData();
    } catch (error) {
      console.error('Delete error:', error);
    }
  }, [clearSelection, deleteItem, detail?.uuid, handleCloseDelete, handleCloseForm, refreshData, selectedObj?.uuid]);

  const handleBulkDelete = useCallback(
    async (uuids: string[]) => {
      try {
        await api.post(`/entities/${entity?.slug}/erp/${QUERY_KEYS.PHIEU_XUAT_KHO}/bulk-delete/`, {
          list: uuids
        });
        await refreshData();
      } catch (error) {
        console.error('Bulk delete error:', error);
        throw error;
      }
    },
    [entity?.slug, refreshData]
  );

  return {
    handleSubmit,
    handleDelete,
    handleBulkDelete
  };
};
